import React, { useState, useRef, useEffect } from 'react';
import StageUploadForm from './StageUploadForm';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const FileManager = ({ teamId, userId, onUploadSuccess }) => {
  const { user } = useAuth();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deliverables, setDeliverables] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [teamName, setTeamName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Load deliverables and team uploads
  useEffect(() => {
    if (teamId) {
      loadDeliverablesAndUploads();
      loadTeamName();
    }
  }, [teamId]);

  const loadTeamName = async () => {
    try {
      const { data, error } = await supabase?.from('teams')?.select('team_name')?.eq('id', teamId)?.single();
      if (error) throw error;
      setTeamName(data?.team_name || '');
    } catch (err) {
      console.error('Error loading team name:', err);
    }
  };

  const loadDeliverablesAndUploads = async () => {
    try {
      setLoading(true);
      setError('');

      // 1️⃣ Fetch all team deliverables
      const { data: deliverablesData, error: deliverablesError } = await supabase?.from('team_deliverables')?.select('id, deliverable_name, due_date, required_upload_count, completed_upload_count, status')?.eq('team_id', teamId)?.order('due_date', { ascending: true });

      if (deliverablesError) throw deliverablesError;

      // 2️⃣ Fetch all uploads for the team (with deliverable + user info)
      const { data: uploadData, error: uploadError } = await supabase?.from('team_deliverable_uploads')?.select(`
          id,
          team_id,
          deliverable_id,
          file_name,
          file_url,
          uploaded_by,
          created_at,
          user_profiles:uploaded_by(full_name),
          team_deliverables:deliverable_id(deliverable_name)
        `)?.eq('team_id', teamId)?.order('created_at', { ascending: false });

      if (uploadError) throw uploadError;

      setDeliverables(deliverablesData || []);
      setUploads(uploadData || []);
    } catch (err) {
      console.error('Error loading deliverables/uploads:', err);
      setError('Failed to load deliverables or files.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    loadDeliverablesAndUploads();
    if (onUploadSuccess) onUploadSuccess();
  };

  const handleDeleteFile = async (fileId, fileUrl) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      // Delete from database
      const { error: dbError } = await supabase?.from('team_deliverable_uploads')?.delete()?.eq('id', fileId);

      if (dbError) throw dbError;

      // Try deleting from storage (extract path from URL)
      if (fileUrl) {
        const urlParts = fileUrl?.split('/storage/v1/object/public/');
        if (urlParts?.length > 1) {
          const fullPath = urlParts?.[1];
          const pathParts = fullPath?.split('/');
          const bucketName = pathParts?.[0];
          const filePath = pathParts?.slice(1)?.join('/');

          const { error: storageError } = await supabase?.storage?.from(bucketName)?.remove([filePath]);
          
          if (storageError) console.warn('Storage delete failed:', storageError);
        }
      }

      loadDeliverablesAndUploads();
    } catch (err) {
      console.error('Error deleting file:', err);
      alert('Failed to delete file.');
    }
  };

  const handleFileClick = async (file) => {
    setSelectedFile(file);
    setShowPreviewModal(true);
    setPreviewLoading(true);
    setPreviewUrl(null);

    try {
      // Extract storage path from file_url
      // file_url format: "user_id/timestamp_filename.ext"
      const filePath = file?.file_url;
      
      // Generate signed URL for preview (valid for 1 hour)
      const { data, error } = await supabase?.storage
        ?.from('team-stage-files')
        ?.createSignedUrl(filePath, 3600);

      if (error) throw error;
      
      setPreviewUrl(data?.signedUrl);
    } catch (err) {
      console.error('Error generating preview URL:', err);
      setError('Failed to load file preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const filePath = file?.file_url;
      
      // Generate signed URL for download
      const { data, error } = await supabase?.storage
        ?.from('team-stage-files')
        ?.createSignedUrl(filePath, 60); // 1 minute expiry for download

      if (error) throw error;

      // Trigger download
      const link = document.createElement('a');
      link.href = data?.signedUrl;
      link.download = file?.file_name;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file.');
    }
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setError('');
  };

  const getFileIcon = (fileName = '') => {
    const ext = fileName?.split('.')?.pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'FileText';
      case 'doc': case'docx': return 'FileText';
      case 'ppt': case'pptx': return 'Presentation';
      case 'jpg': case'jpeg': case'png': return 'Image';
      case 'zip': return 'Archive';
      default: return 'File';
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">File Manager</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2"
        >
          <Icon name="Upload" size={16} />
          Upload File
        </Button>
      </div>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* File Upload Form - Moved Above Deliverables */}
      <div className="mb-6">
        <StageUploadForm
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          teamId={teamId}
          teamName={teamName}
          userId={userId}
          onUploadSuccess={handleUploadSuccess}
        />
      </div>

      {/* Deliverables Section */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading files...</span>
        </div>
      ) : (
        <div className="space-y-8">
          {deliverables?.map((deliverable) => {
            const deliverableFiles = uploads?.filter((f) => f?.deliverable_id === deliverable?.id);

            return (
              <div key={deliverable?.id}>
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="FileText" size={20} className="text-primary" />
                  <h4 className="text-md font-medium text-foreground">
                    {deliverable?.deliverable_name}
                  </h4>
                  <span className="text-sm text-muted-foreground">
                    ({deliverableFiles?.length})
                  </span>
                </div>
                {deliverableFiles?.length === 0 ? (
                  <div className="p-4 border border-dashed border-border rounded-lg text-center">
                    <p className="text-muted-foreground">
                      No files uploaded yet for {deliverable?.deliverable_name}.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {deliverableFiles?.map((file) => {
                      const isUploader = user?.id === file?.uploaded_by;
                      
                      return (
                        <div
                          key={file?.id}
                          className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <Icon
                                name={getFileIcon(file?.file_name)}
                                size={16}
                                className="text-primary flex-shrink-0"
                              />
                              <button
                                onClick={() => handleFileClick(file)}
                                className="text-sm font-medium text-foreground hover:text-primary truncate text-left"
                              >
                                {file?.file_name}
                              </button>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDownload(file)}
                                className="h-6 w-6 text-primary hover:text-primary"
                                title="Download file"
                              >
                                <Icon name="Download" size={14} />
                              </Button>
                              {isUploader && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteFile(file?.id, file?.file_url)}
                                  className="h-6 w-6 text-error hover:text-error"
                                >
                                  <Icon name="Trash2" size={14} />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground">
                              <span className="font-medium">Deliverable:</span> {file?.team_deliverables?.deliverable_name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              <span className="font-medium">Uploaded by:</span> {file?.user_profiles?.full_name || 'Unknown User'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              <span className="font-medium">Date:</span> {formatDateTime(file?.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {deliverables?.length === 0 && (
            <div className="p-8 border border-dashed border-border rounded-lg text-center">
              <Icon name="Inbox" size={48} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">No deliverables found for this team.</p>
            </div>
          )}
        </div>
      )}

      {/* File Preview Modal */}
      {showPreviewModal && (
        <>
          {/* Modal Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
            onClick={closePreviewModal}
          />
          {/* Modal Content */}
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div 
                className="relative bg-background rounded-lg shadow-elevated w-full max-w-4xl max-h-[90vh] overflow-hidden animate-slide-in"
                onClick={(e) => e?.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between z-10">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-foreground truncate">{selectedFile?.file_name}</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Uploaded by {selectedFile?.user_profiles?.full_name} • {formatDateTime(selectedFile?.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      onClick={() => handleDownload(selectedFile)}
                      variant="outline"
                      size="sm"
                      className="flex items-center space-x-2"
                    >
                      <Icon name="Download" size={16} />
                      <span>Download</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={closePreviewModal}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Icon name="X" size={24} />
                    </Button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                  {previewLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Icon name="Loader2" size={32} className="text-primary animate-spin" />
                      <span className="ml-3 text-muted-foreground">Loading preview...</span>
                    </div>
                  ) : error ? (
                    <div className="bg-error/10 border border-error/20 rounded-lg p-6 flex items-center space-x-3">
                      <Icon name="AlertTriangle" size={24} className="text-error" />
                      <p className="text-error">{error}</p>
                    </div>
                  ) : previewUrl ? (
                    <div className="space-y-4">
                      {/* File Info */}
                      <div className="bg-muted rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-muted-foreground uppercase tracking-wide">Deliverable</label>
                            <p className="text-sm font-medium text-foreground mt-1">
                              {selectedFile?.team_deliverables?.deliverable_name}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground uppercase tracking-wide">Uploaded By</label>
                            <p className="text-sm font-medium text-foreground mt-1">
                              {selectedFile?.user_profiles?.full_name}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* File Preview */}
                      <div className="border border-border rounded-lg overflow-hidden">
                        {selectedFile?.file_name?.toLowerCase()?.endsWith('.pdf') ? (
                          <iframe
                            src={previewUrl}
                            className="w-full h-[600px]"
                            title="PDF Preview"
                          />
                        ) : selectedFile?.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <img
                            src={previewUrl}
                            alt={selectedFile?.file_name}
                            className="w-full h-auto"
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center py-12 space-y-4">
                            <Icon name="FileText" size={64} className="text-muted-foreground" />
                            <p className="text-muted-foreground">Preview not available for this file type</p>
                            <Button
                              onClick={() => handleDownload(selectedFile)}
                              variant="default"
                              className="flex items-center space-x-2"
                            >
                              <Icon name="Download" size={16} />
                              <span>Download to view</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FileManager;